// RealtimeService: JS-only, refactor target for OpenAiConnection with clearer contract.
// This is a skeleton. It mirrors current behavior but adds reconnection/backoff and event buffering.

import EventEmitter from "wolfy87-eventemitter";
import { RealtimeRelay } from "./realtimeRelay.service";
import config from "../config";

export const REALTIME_STATES = {
  STOPPED: "Stopped",
  CONNECTING: "Connecting",
  CONNECTED: "Connected",
  DISCONNECTED: "Disconnected",
  RECONNECTING: "Reconnecting",
};

const DEFAULT_BASE_URL = "https://cvlr-ai-1-eastus2.openai.azure.com/openai/v1/realtime/calls";
const DEFAULT_MODEL = "gpt-realtime-2";
export const DEFAULT_REALTIME_OUTPUT = {
  voice: "marin",
  speed: 1,
};
export const REALTIME_VOICE_OPTIONS = ["alloy", "ash", "ballad", "coral", "echo", "sage", "shimmer", "verse", "marin", "cedar"];

export function normalizeRealtimeOutputConfig(output = {}) {
  const voice = typeof output.voice === "string" && output.voice.trim()
    ? output.voice.trim()
    : DEFAULT_REALTIME_OUTPUT.voice;
  const speedValue = Number(output.speed);
  const speed = Number.isFinite(speedValue)
    ? Math.min(1.5, Math.max(0.25, speedValue))
    : DEFAULT_REALTIME_OUTPUT.speed;
  return { voice, speed };
}

function backoffDelay(attempt, base = 500, cap = 10000) {
  const exp = Math.min(cap, base * 2 ** attempt);
  const jitter = Math.random() * 0.3 + 0.85;
  return Math.floor(exp * jitter);
}

// Max consecutive automatic re-issues of the last response.create after a transient
// Azure-side failure, before giving up and emitting a serviceError.
const MAX_RESPONSE_RETRY_ATTEMPTS = 3;

// Max consecutive re-voices of an unvoiced text response, before giving up. Bounds a
// persistent text -> re-voice -> text loop from the model.
const MAX_REVOICE_ATTEMPTS = 2;

/**
 * Classify an Azure realtime error (the `error` event payload, or a failed
 * response's status_details.error) into a recovery strategy.
 * @param {{ error?: { type?: string, code?: string } }} event
 * @returns {'transient' | 'fatal-session' | 'ignore'}
 */
export function classifyRealtimeError(event) {
  const code = event?.error?.code || event?.error?.type || "";
  switch (code) {
    case "session_expired":
    case "invalid_session_token":
    case "invalid_api_key":
    case "token_expired":
      return "fatal-session";
    case "response_cancel_not_active":
    case "conversation_already_has_active_response":
    case "input_audio_buffer_commit_empty":
      // Expected noise from our own cancel/clear sequences — never retry these.
      return "ignore";
    case "server_error":
    case "rate_limit_exceeded":
      return "transient";
    default:
      // Never silently fatal: an unrecognized Azure-side error is treated as transient.
      return "transient";
  }
}

function withTimeout(pf, ms = 15000, label = "operation") {
  const ctl = new AbortController();
  const t = setTimeout(() => ctl.abort(), ms);
  return (async () => {
    try {
      return await pf(ctl.signal);
    } finally {
      clearTimeout(t);
    }
  })().catch((e) => {
    throw new Error(`${label} timeout/abort: ${e?.message || e}`);
  });
}

export class RealtimeService {
  /**
   * @param {{ baseUrl?: string, model?: string }} [opts]
   */
  constructor(opts = {}) {
    this.baseUrl = opts.baseUrl || DEFAULT_BASE_URL;
    this.model = opts.model || DEFAULT_MODEL;
    this.output = normalizeRealtimeOutputConfig(opts.output || DEFAULT_REALTIME_OUTPUT);
    this.emitter = new EventEmitter();
    this._state = REALTIME_STATES.STOPPED;
    this._messageQueue = [];
    this._connecting = false;
    this._closed = false;
    this._attempt = 0;
    this._part = 0;
    this._section = null;
    this._sectionStartTimeMs = null;
    this._startTimeMs = null;
    this._activeResponseId = null;
    this._activeOutputResponseId = null;
    this._finishRequested = false;
    this._captions = "";
    this._hbStop = null; // deprecated: heartbeat removed
    this._reconnectTimer = null;
    this._closingForReconnect = false; // true while an intentional reconnect is in progress
    this._naturalWrap = null; // { seconds, delayMs, fallbackMs, armedAt, executed, fallbackTimer, scheduledTimer }
    // Dedup guards
    this._initialSentForSectionId = null;
    this._initialSentAt = 0;
    // Response-level retry: re-issue the last response.create on a transient Azure-side
    // failure (the connection stays healthy, so the transport reconnect loop never fires).
    this._lastResponseArgs = null;
    this._responseRetryCount = 0;
    this._responseRetryTimer = null;
    // Tracks whether the most recent _open() call was initiated from a reconnect path
    this._lastOpenWasReconnect = false;
    // Backend relay: forwards Azure events filtered out of the browser by webrtcfilter=on.
    // Relayed events are routed through the same _handleMessage switch as data-channel events.
    // Gated by config; when off, the legacy direct-Azure flow is used (no filter, no relay).
    this._webrtcFilter = opts.webrtcFilter ?? config.webrtcFilterEnabled ?? false;
    this._applicationId = null;
    this._callId = null;
    this._lastRelayError = null;
    this.relay = new RealtimeRelay({
      onEvent: (event) => this._handleMessage({ data: JSON.stringify(event) }),
      onLog: (tag, payload) => this._log(tag, payload),
      onError: (detail) => this._onRelayError(detail),
    });
    // Readiness handshake state (reset per _open): data channel open + session.updated (via relay).
    this._dcOpen = false;
    this._sessionConfigured = false;
    this._readyResolve = null;
    this._readyTimeout = null;
    // gpt-realtime sometimes answers as a text item (delivered on the data channel via
    // response.output_text.*) with no audio, so the candidate hears silence. Capture text output
    // per response id and re-voice it if the response completes unvoiced.
    this._textBuffers = new Map();
    this._revoiceCount = 0;
    this._userSpeaking = false;
    // Count of candidate utterances whose async Whisper transcription
    // (conversation.item.input_audio_transcription.completed) has not yet arrived. Used to hold
    // a section rotation/finish until the candidate's final answer is captured (see
    // waitForInputTranscriptionsSettled). Reset on teardown so it can't leak across sections.
    this._pendingInputTranscriptions = 0;
    this._inputSettleResolvers = [];
  }

  on(...args) { this.emitter.addListener(...args); }
  off(...args) { if (args.length === 1) this.emitter.removeAllListeners(...args); else this.emitter.removeListener(...args); }

  get state() { return this._state; }
  _setState(s) { this._state = s; this.emitter.emitEvent("state", [s]); }

  /**
   * @param {{ key: string, inputStream: MediaStream, audioEl: HTMLAudioElement, part: number, startTimeMs: number, tools?: any, whisperModel?: string, output?: { voice?: string, speed?: number } }} args
   */
  async connect({ key, inputStream, audioEl, part, startTimeMs, tools, whisperModel = "whisper-1", output, section = null, sectionStartTimeMs = null, applicationId = null }) {
    if (this._connecting) return;
    this._closed = false;
    this._lastOpenWasReconnect = false;
    this._attempt = 0;
    this._part = part || 0;
    this._applicationId = applicationId || this._applicationId;
    this._section = section || null;
    this._sectionStartTimeMs = sectionStartTimeMs || Date.now();
    this._startTimeMs = startTimeMs || Date.now();
    this.audioEl = audioEl;
    this.inputStream = inputStream;
    this.key = key;
    this.tools = tools;
    this.whisperModel = whisperModel;
    this.output = normalizeRealtimeOutputConfig(output || this.output);
    await this._open();
  }

  setOutputConfig(output = {}) {
    const previous = this.output;
    const next = normalizeRealtimeOutputConfig({
      ...previous,
      ...output,
    });
    const patch = {};
    if (next.voice !== previous.voice) patch.voice = next.voice;
    if (next.speed !== previous.speed) patch.speed = next.speed;
    this.output = next;
    if (this.dc?.readyState === "open" && Object.keys(patch).length) {
      this._send({
        type: "session.update",
        session: {
          type: "realtime",
          audio: {
            output: patch,
          },
        },
      });
    }
    return this.output;
  }

  _buildSessionConfig() {
    return {
      type: "realtime",
      // Pin audio at the session level so server-VAD-created responses default to speech, not
      // text (mirrors the per-response output_modalities in requestResponse). Reduces the
      // text-only-answer glitch that leaves the candidate hearing silence.
      output_modalities: ["audio"],
      tools: this.tools || [],
      audio: {
        input: {
          transcription: { model: this.whisperModel || "whisper-1" },
          turn_detection: { type: "server_vad", silence_duration_ms: 1500 },
        },
        output: {
          voice: this.output.voice,
          speed: this.output.speed,
        },
      },
    };
  }

  /**
   * Reconnect with a new token/section, keeping existing input/audio elements.
   * @param {{ key: string, section?: any, sectionStartTimeMs?: number }} args
   */
  async reconnect({ key, section = null, sectionStartTimeMs = null, applicationId = null }) {
    this.key = key || this.key;
    if (applicationId) this._applicationId = applicationId;
    if (section) {
      this._section = section;
      // reset initial message dedup guard when section changes
      try {
        const sid = section && (section._id || section.id || null);
        if (sid && sid !== this._initialSentForSectionId) {
          this._initialSentForSectionId = null;
          this._initialSentAt = 0;
        }
      } catch { /* noop */ }
    }
    if (sectionStartTimeMs) this._sectionStartTimeMs = sectionStartTimeMs;
    this._cancelReconnectTimer();
    this._setState(REALTIME_STATES.RECONNECTING);
    this._closingForReconnect = true;
    this._teardownKeepInput();
    this._attempt = 0;
    this._closed = false;
    this._lastOpenWasReconnect = true;
    try {
      await this._open();
    } finally {
      // Reconnect finished (success or failure). If it failed, _scheduleReconnect will handle retries.
      this._closingForReconnect = false;
    }
  }

  async _open() {
    this._log("OpenStart", { state: this._state, attempt: this._attempt, closed: this._closed, connecting: this._connecting });
    this._connecting = true;
    this._setState(REALTIME_STATES.CONNECTING);
    try {
      const pc = new RTCPeerConnection();
      this.pc = pc;
      this.audioEl.autoplay = true;
      pc.ontrack = (e) => {
        this.audioEl.srcObject = e.streams[0];
        this._remoteAudioTrack = e.streams[0].getAudioTracks()[0];
        this._log("RemoteTrack", {
          streamId: e.streams?.[0]?.id,
          audioTracks: e.streams?.[0]?.getAudioTracks?.().length,
          videoTracks: e.streams?.[0]?.getVideoTracks?.().length,
          trackId: this._remoteAudioTrack?.id,
        });
        this.emitter.emitEvent("remoteTrack", [e.streams[0]]);
      };
      pc.addTrack(this.inputStream.getTracks()[0]);

      pc.onconnectionstatechange = () => {
        const st = pc.connectionState;
        // Ignore state changes from stale peer connections
        if (pc !== this.pc) return;
        this._log("PeerConnectionStateChange", { state: st });
        if (st === "connected") {
          this._setState(REALTIME_STATES.CONNECTED);
        } else if (st === "disconnected" || st === "failed") {
          this._setState(REALTIME_STATES.DISCONNECTED);
          // Avoid auto-reconnect if we're intentionally reconnecting
          if (!this._closingForReconnect) {
            // Mirror legacy behavior: always schedule reconnect after a short timeout
            this._log("ScheduleReconnectOnPeerState", { state: st, delayMs: 2000 });
            setTimeout(() => {
              // If we've since closed or started an intentional reconnect, skip
              if (this._closed || this._closingForReconnect) return;
              this._scheduleReconnect();
            }, 2000);
          }
        }
      };

      // Data channel
      const dc = pc.createDataChannel("oai-events");
      this.dc = dc;
      dc.addEventListener("message", (e) => this._handleMessage(e));
      dc.addEventListener("close", () => this._onDataChannelClose());
      dc.addEventListener("error", (e) => this._log("DataChannelError", e));

      // Readiness resolves when the data channel opens AND session.updated arrives.
      // With webrtcfilter=on, session.updated is delivered via the relay, not the data
      // channel, so _handleMessage's "session.updated" case drives the second condition.
      this._dcOpen = false;
      this._sessionConfigured = false;
      const readyPromise = new Promise((resolve, reject) => {
        this._readyResolve = resolve;
        this._readyTimeout = setTimeout(() => {
          this._readyResolve = null;
          let message = "Datachannel or session.update timeout";
          if (this._webrtcFilter) {
            // session.updated now arrives via the relay. If the data channel opened but the
            // session was never configured, the relay/backend is the likely cause — say so.
            const relayStatus = this.relay.describe();
            this._log("ReadyTimeout", {
              dcOpen: this._dcOpen,
              sessionConfigured: this._sessionConfigured,
              relay: relayStatus,
              lastRelayError: this._lastRelayError || null,
            });
            message = `Realtime not ready: dataChannelOpen=${this._dcOpen}, sessionConfigured=${this._sessionConfigured}; ${relayStatus}`;
          }
          reject(new Error(message));
        }, 10000);
      });

      dc.addEventListener("open", () => {
        this._log("DataChannelOpen", { isReconnect: !!this._lastOpenWasReconnect, state: this._state, attempt: this._attempt });
        this._dcOpen = true;
        // Configure the session; the ordered data channel guarantees later events apply after it.
        this._send({ type: "session.update", session: this._buildSessionConfig() });
        this._checkReady();
      });

      // SDP offer/answer
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const sdpUrl = this._webrtcFilter ? `${this.baseUrl}?webrtcfilter=on` : this.baseUrl;
      const sdpResponse = await withTimeout((signal) => fetch(sdpUrl, {
        method: "POST",
        body: offer.sdp,
        headers: {
          Authorization: `Bearer ${this.key}`,
          "Content-Type": "application/sdp",
        },
        signal,
      }), 15000, "SDP exchange");

      if (this._webrtcFilter) {
        // Start the backend relay before applying the answer, so the observer attaches before
        // the data channel opens and emits session.updated (avoids missing the readiness ACK).
        const callId = this._extractCallId(sdpResponse);
        this._callId = callId;
        if (!callId) this._log("RelayNoCallId", { note: "Location header missing/unreadable" });
        this.relay.start({
          applicationId: this._applicationId || null,
          sectionId: this._section?._id || this._section?.id || null,
          callId,
        });
      }

      const answer = { type: "answer", sdp: await sdpResponse.text() };
      await pc.setRemoteDescription(answer);

      // Wait for datachannel to open and session to be configured
      await readyPromise;

      this._attempt = 0;
    } catch (e) {
      this._log("OpenError", { message: e?.message || String(e), attempt: this._attempt, state: this._state, closed: this._closed, closingForReconnect: this._closingForReconnect });
      // If the SDP exchange fails (e.g., no internet), keep retrying using the
      // same backoff-based reconnect loop, unless we've been closed or are in
      // the middle of an intentional reconnect.
      if (!this._closed && !this._closingForReconnect) {
        this._scheduleReconnect();
      }
    } finally {
      this._connecting = false;
    }
  }

  // Surface a backend/relay-side failure with detail: log it, console.error it (so it is
  // visible/diagnosable in prod), and emit a `relayError` event for any consumer/telemetry.
  // This deliberately distinguishes a backend problem from a browser-side WebRTC failure.
  _onRelayError(detail) {
    this._lastRelayError = detail || null;
    this._log("RelayError", detail);
    try { console.error("[RealtimeService] relay/backend error:", detail?.message || detail); } catch { /* noop */ }
    try { this.emitter.emitEvent("relayError", [detail]); } catch { /* noop */ }
  }

  // Resolve the readiness promise once both the data channel is open and the session
  // has been configured (session.updated received via the relay).
  _checkReady() {
    if (this._dcOpen && this._sessionConfigured) {
      if (this._readyTimeout) { clearTimeout(this._readyTimeout); this._readyTimeout = null; }
      if (this._readyResolve) { const resolve = this._readyResolve; this._readyResolve = null; resolve(); }
    }
  }

  // Extract the Azure call_id from the SDP response Location header (e.g. ".../calls/rtc_abc" -> "rtc_abc").
  _extractCallId(response) {
    try {
      const loc = response?.headers?.get?.("Location") || response?.headers?.get?.("location") || "";
      if (!loc) return null;
      const seg = loc.split("?")[0].replace(/\/+$/, "").split("/").filter(Boolean).pop();
      return seg || null;
    } catch { return null; }
  }

  _onDataChannelClose() {
    this._log("DataChannelClose");
    if (this._closed) return;
    if (this._closingForReconnect) return; // we intentionally closed; reconnect will be handled by reconnect()
    // Defensive cleanup: cancel any active assistant generation and clear audio buffers
    try {
      if (this._activeResponseId) {
        this._send({ type: "response.cancel", response_id: this._activeResponseId });
      }
      this._clearOutputAudio();
    } catch { /* ignore */ }
    // Avoid double-scheduling while already connecting/reconnecting
    if (this._connecting || this._state === REALTIME_STATES.RECONNECTING) return;
    this._scheduleReconnect();
  }

  _scheduleReconnect() {
    if (this._closed) return;
    if (this._reconnectTimer) return; // already scheduled
    this._attempt += 1;
    this._setState(REALTIME_STATES.RECONNECTING);
    this._lastOpenWasReconnect = true;
    const wait = backoffDelay(this._attempt);
    this._log("ScheduleReconnect", { attempt: this._attempt, waitMs: wait, state: this._state });
    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = null;
      if (this._closed) return;
      this._log("ReconnectTimerFired", { attempt: this._attempt, state: this._state });
      this._teardownKeepInput();
      this._open();
    }, wait);
  }

  _send(obj) {
    const payload = JSON.stringify({ ...obj, event_id: obj.event_id || crypto.randomUUID() });
    this._log("SendEvent", obj);
    if (this.dc && this.dc.readyState === "open") {
      this.dc.send(payload);
    } else {
      this._messageQueue.push(payload);
    }
  }

  get elapsedTimeMs() { return Math.max(0, Date.now() - (this._startTimeMs || Date.now())); }
  get sectionElapsedTimeMs() { return Math.max(0, Date.now() - (this._sectionStartTimeMs || Date.now())); }

  _markOutputStarted(responseId = null) {
    const active = this._activeOutputResponseId;
    // Same response already active — don't reset captions mid-utterance.
    if (active && (active === true || !responseId || active === responseId)) return;
    // Nothing active, or a DIFFERENT response is starting (the previous one was interrupted/cut
    // and we never received its stop — e.g. a barge-in's output_audio_buffer.cleared, which the
    // relay/data channel never delivers). Begin a fresh turn so captions don't accumulate.
    this._activeOutputResponseId = responseId || true;
    this._captions = "";
    this.emitter.emitEvent("captions", [""]);
    this.emitter.emitEvent("outputStarted");
  }

  _markOutputStopped(responseId = null) {
    if (!this._activeOutputResponseId) return;
    if (
      responseId &&
      this._activeOutputResponseId !== true &&
      this._activeOutputResponseId !== responseId
    ) return;
    this._activeOutputResponseId = null;
    this.emitter.emitEvent("outputStopped");
  }

  // Cut the current assistant audio. Clears captions too, so the on-screen text for the
  // audio being discarded doesn't linger past the voice (barge-in / wrap / finish / close).
  _clearOutputAudio() {
    this._send({ type: "output_audio_buffer.clear" });
    // We just cut the audio; the server's output_audio_buffer.cleared won't reach us, so clear
    // the output tracking here too (else _activeOutputResponseId sticks across the next turn).
    this._markOutputStopped();
    if (this._captions) { this._captions = ""; this.emitter.emitEvent("captions", [""]); }
  }

  requestResponse({ instructions, input } = {}) {
    // Remember the request so a transient Azure-side failure can be retried.
    // A fresh app-initiated request resets the consecutive-failure counter.
    this._lastResponseArgs = { instructions, input };
    this._responseRetryCount = 0;
    const response = { output_modalities: ["audio"] };
    if (instructions) response.instructions = instructions;
    if (input) response.input = input;
    this._send({ type: "response.create", response });
  }

  // Re-issue the last response.create without disturbing the retry counter.
  _reissueLastResponse() {
    const { instructions, input } = this._lastResponseArgs || {};
    const response = { output_modalities: ["audio"] };
    if (instructions) response.instructions = instructions;
    if (input) response.input = input;
    this._log("ResponseRetry", { attempt: this._responseRetryCount });
    this._send({ type: "response.create", response });
  }

  /**
   * Schedule a bounded re-issue of the last response after a transient Azure-side failure.
   * Distinct from transport reconnect: the connection is healthy, we just ask the model again.
   * @param {string} reason
   */
  _scheduleResponseRetry(reason) {
    if (this._closed || !this._lastResponseArgs) return;
    if (this._responseRetryTimer) return; // already scheduled
    // Transport recovery owns the retry while reconnecting/connecting.
    if (this._connecting || this._state === REALTIME_STATES.RECONNECTING) return;
    if (this._responseRetryCount >= MAX_RESPONSE_RETRY_ATTEMPTS) {
      this._log("ResponseRetryExhausted", { reason, attempts: this._responseRetryCount });
      try { this.emitter.emitEvent("serviceError", [{ kind: "response_retry_exhausted", reason, attempts: this._responseRetryCount }]); } catch (e) { void e; }
      this._responseRetryCount = 0;
      return;
    }
    this._responseRetryCount += 1;
    const wait = backoffDelay(this._responseRetryCount, 300, 3000);
    this._log("ScheduleResponseRetry", { reason, attempt: this._responseRetryCount, waitMs: wait });
    this._responseRetryTimer = setTimeout(() => {
      this._responseRetryTimer = null;
      if (this._closed) return;
      if (this._activeResponseId) {
        // A new turn already started (e.g. server VAD) — already recovered.
        this._log("ResponseRetrySkippedActive", { activeResponseId: this._activeResponseId });
        return;
      }
      if (this.dc?.readyState !== "open") {
        // Transport is down; its own reconnect loop owns recovery.
        this._log("ResponseRetrySkippedDcClosed", {});
        return;
      }
      this._reissueLastResponse();
    }, wait);
  }

  _cancelResponseRetry() {
    try { if (this._responseRetryTimer) clearTimeout(this._responseRetryTimer); } catch (e) { void e; }
    this._responseRetryTimer = null;
    this._responseRetryCount = 0;
  }

  /**
   * gpt-realtime occasionally emits a turn as a text item (no audio), so the candidate hears
   * silence. On a completed response that produced text output, re-issue it as an audio-only
   * response so the question is actually voiced. Consumes the captured text for the response.
   * @param {any} event  the response.done event
   * @param {string|null} responseId
   */
  _maybeRevoiceUnvoicedText(event, responseId) {
    const text = responseId ? this._textBuffers.get(responseId) : null;
    if (responseId) this._textBuffers.delete(responseId);
    // Only re-voice a genuinely completed turn. A cancelled/incomplete turn was cut on purpose
    // (barge-in, wrap, finish) — dropping its buffer above is enough.
    if (event?.response?.status !== "completed") return;
    if (!text || !text.trim()) {
      // A properly voiced turn resets the consecutive re-voice counter.
      this._revoiceCount = 0;
      return;
    }
    // Section is ending (a tool call was produced or a finish was requested): don't re-voice.
    const outputs = event?.response?.output;
    const hasFunctionCall = Array.isArray(outputs) && outputs.some((o) => o?.type === "function_call");
    if (hasFunctionCall || this._finishRequested) {
      this._log("UnvoicedTextSkipped", { reason: hasFunctionCall ? "function_call" : "finish", chars: text.length });
      return;
    }
    // The user is already talking — a new VAD turn is coming; don't collide with it.
    if (this._userSpeaking) {
      this._log("UnvoicedTextSkipped", { reason: "user_speaking", chars: text.length });
      return;
    }
    // Bound consecutive re-voices so a persistent text -> re-voice -> text loop can't run away.
    if (this._revoiceCount >= MAX_REVOICE_ATTEMPTS) {
      this._log("UnvoicedTextRevoiceExhausted", { attempts: this._revoiceCount, chars: text.length });
      return;
    }
    this._revoiceCount += 1;
    this._log("UnvoicedTextDetected", { attempt: this._revoiceCount, chars: text.length });
    this.requestResponse({
      instructions: "Speak the following aloud as the interviewer. Audio only. Do not mention any error or that this was repeated.",
      input: [{ type: "message", role: "system", content: [{ type: "input_text", text: `The candidate could not hear your previous message because it was produced as text with no audio. Say it aloud now, verbatim: "${text.trim()}"` }] }],
    });
  }

  sendInitialMessage() {
    // Only send the initial "Start the interview." prompt on the first connection
    // for this section. On reconnects, callers must use sendReconnectMessage instead.
    try {
      const now = Date.now();
      const sid = this._section && (this._section._id || this._section.id || null);
      // If we're in a reconnecting state, skip the initial message entirely.
      if (this._state === REALTIME_STATES.RECONNECTING) {
        this._log("InitialMessageSkippedOnReconnect", { sectionId: sid });
        return;
      }
      if (sid && this._initialSentForSectionId === sid && (now - this._initialSentAt) < 2000) {
        this._log("InitialMessageDedupSkipped", { sectionId: sid, sinceMs: now - this._initialSentAt });
        return;
      }
      if (sid) {
        this._initialSentForSectionId = sid;
        this._initialSentAt = now;
      } else {
        // If no section id, still rate-limit to 2s window
        if ((now - this._initialSentAt) < 2000) {
          this._log("InitialMessageDedupSkipped", { sectionId: null, sinceMs: now - this._initialSentAt });
          return;
        }
        this._initialSentAt = now;
      }
    } catch { /* noop */ }
    this.requestResponse({
      input: [{ type: "message", role: "system", content: [{ type: "input_text", text: "Start the interview." }] }]
    });
  }

  async sendReconnectMessage({ transcriptsForPart = "", repeatLastQuestion = false } = {}) {
    // If there's an active response, cancel before resuming the conversation
    if (this._activeResponseId) {
      this._send({ type: "response.cancel", response_id: this._activeResponseId });
      await new Promise((r) => setTimeout(r, 300));
    }
    try {
      this._log("Reconnect", {
        transcriptChars: typeof transcriptsForPart === "string" ? transcriptsForPart.length : 0,
        repeatLastQuestion: !!repeatLastQuestion,
      });
    } catch { /* logging is best-effort */ }
    const text = `You’ve been reconnected after a pause.\nInform the candidate that you are back and resuming the interview.\nCONSIDER THE PREVIOUS TRANSCRIPT.\n${repeatLastQuestion ? "Repeat the last question." : "Evaluate the last answer and: If the last answer was clear and complete, naturally move on to the next question. If not, ask a concise follow-up or request a more specific example."}\nMaintain a professional and conversational tone.\n\nTHE TRANSCRIPT SO FAR:\n\n====\n${transcriptsForPart}\n====`;
    this.requestResponse({
      input: [{ type: "message", role: "system", content: [{ type: "input_text", text }] }]
    });
  }

  async sendFinishRequest({ force = false, reason = "time" } = {}) {
    this._finishRequested = true;
    // If there is an active response, cancel first and clear output buffer to mirror legacy behavior
    if (this._activeResponseId) {
      this._send({ type: "response.cancel", response_id: this._activeResponseId });
      await new Promise((r) => setTimeout(r, 100));
    }
    this._clearOutputAudio();
    await new Promise((r) => setTimeout(r, 100));

    let text;
    let instructions;
    if (force) {
      text = "FORGET ALL PREVIOUS INSTRUCTIONS. STOP THE INTERVIEW. DO NOT SAY ANYTHING ELSE and EXECUTE IMMEDIATELY function_call/tool \"interview_finished\".";
      instructions = "STOP immediately. Do NOT speak. Do NOT say goodbye. Call the interview_finished tool RIGHT NOW with no additional output.";
    } else if (reason === "last-section") {
      text = "This concludes the interview. Thank the candidate and say goodbye in a concise and polite way. After saying goodbye, execute function_call \"interview_finished\".";
      instructions = "This is the final section. Disregard any prior instructions about continuing or asking more questions. Thank the candidate warmly, say a polite goodbye, and then call the interview_finished tool. Do NOT ask any more questions.";
    } else {
      text = "Say that unfortunately the time for the interview is up. Say in polite words: thanks and goodbye. Finish the interview. After saying goodbye, execute function_call \"interview_finished\".";
      instructions = "Time is up. Disregard any prior instructions about continuing the interview or asking more questions. Tell the candidate time is up, say a brief goodbye, and then call the interview_finished tool. Do NOT continue the interview under any circumstances.";
    }
    this.requestResponse({
      instructions,
      input: [{ type: "message", role: "system", content: [{ type: "input_text", text }] }]
    });
  }

  _handleMessage(e) {
    const event = JSON.parse(e.data);
    // Do not log noisy *\.delta events and session events, but still process them below
    const eventsToIgnore = [
      "response.content_part.done",
      "response.audio_transcript.done",
      "response.output_audio_transcript.done",
      "response.content_part.added",
      "response.output_item.added",
    ];
    try {
      const t = String(event?.type || "");
      if (!t.endsWith(".delta") && !t.startsWith("session.") && !eventsToIgnore.includes(t))
        this._log("RecvEvent", event);
    } catch { /* ignore logging errors */ }
    // Mirror event routing similar to OpenAiConnection
    switch (event.type) {
      case "session.updated":
        // Delivered via the relay (filtered off the data channel by webrtcfilter=on).
        // Marks the session configured -> readiness ACK.
        if (!this._sessionConfigured) {
          this._log("SessionUpdated", { isReconnect: !!this._lastOpenWasReconnect, event });
          this._sessionConfigured = true;
          // Flush buffered messages now that the session is configured.
          while (this._messageQueue.length && this.dc?.readyState === "open") {
            this.dc.send(this._messageQueue.shift());
          }
          this.emitter.emitEvent("ready", [{ isReconnect: !!this._lastOpenWasReconnect }]);
          this._checkReady();
        }
        break;
      case "input_audio_buffer.speech_started":
        this._userSpeaking = true;
        // A barge-in interrupts the assistant: the server clears its output buffer and emits
        // output_audio_buffer.cleared, which we never receive — so treat user speech as the
        // assistant's stop. Without this, _activeOutputResponseId sticks (caption overflow +
        // stale assistantSpeaking/auto-mute). No-op when the assistant isn't speaking.
        this._markOutputStopped();
        this.emitter.emitEvent("speechStarted");
        break;
      case "response.created":
        this._activeResponseId = event?.response?.id || null;
        // If a natural quick-wrap is armed, schedule a delayed cancel when the natural response starts
        try {
          if (this._naturalWrap && !this._naturalWrap.executed && this._activeResponseId) {
            const { delayMs = 30 } = this._naturalWrap;
            this._log("NaturalWrap", { step: "response.created", response_id: this._activeResponseId, delayMs, ts: Date.now() });
            // Clear any previous scheduled timer to avoid duplicates
            if (this._naturalWrap.scheduledTimer) {
              clearTimeout(this._naturalWrap.scheduledTimer);
            }
            this._naturalWrap.scheduledTimer = setTimeout(async () => {
              if (!this._naturalWrap || this._naturalWrap.executed) return;
              this._naturalWrap.executed = true;
              // cancel active response, clear audio, then send quick-wrap in non-preemptive mode
              try {
                if (this._activeResponseId) {
                  this._log("NaturalWrap", { step: "cancelling", response_id: this._activeResponseId, ts: Date.now() });
                  this._send({ type: "response.cancel", response_id: this._activeResponseId });
                  await new Promise((r) => setTimeout(r, 150));
                }
                this._log("NaturalWrap", { step: "clearing_output_buffer", ts: Date.now() });
                this._clearOutputAudio();
                await new Promise((r) => setTimeout(r, 150));
                const seconds = this._naturalWrap.seconds ?? 4;
                this._log("NaturalWrap", { step: "sending_quick-wrap", seconds, ts: Date.now() });
                await this.sendQuickWrapAndFinishPart({ seconds, preemptive: false });
              } catch (err) {
                this._log("NaturalWrapError", err);
              } finally {
                // cleanup timers
                try { if (this._naturalWrap?.fallbackTimer) clearTimeout(this._naturalWrap.fallbackTimer); } catch (e) { void e; }
                this._naturalWrap = null;
              }
            }, Math.max(0, delayMs));
          }
        } catch (err) { this._log("NaturalWrapError", err); }
        break;
      case "response.done": {
        // Audio playout end for a COMPLETED response is signaled by output_audio_buffer.stopped
        // (allowlisted, on the data channel, fires on natural drain). With webrtcfilter=on,
        // response.done arrives via the relay and can land BEFORE the audio finishes playing, so
        // it must NOT drive the stop for completed responses (that caused premature auto-unmute).
        // For cancelled/failed/incomplete responses the buffer emits output_audio_buffer.cleared
        // (filtered out), so response.done stays their stop signal.
        const doneResponseId = event?.response?.id || this._activeResponseId || null;
        if (event?.response?.status !== "completed") {
          this._markOutputStopped(event?.response?.id || this._activeResponseId);
        }
        this._activeResponseId = null;
        // An Azure-side generation failure surfaces as status "failed". Our requests are
        // well-formed, so retry the response (bounded) rather than dropping the turn.
        if (event?.response?.status === "failed") {
          if (doneResponseId) this._textBuffers.delete(doneResponseId); // drop any captured text; don't re-voice a failed turn
          const errObj = event?.response?.status_details?.error || null;
          // A bare "failed" with no detail is itself a strong Azure-side signal -> transient.
          const classification = errObj ? classifyRealtimeError({ error: errObj }) : "transient";
          this._log("ResponseFailed", { classification, status_details: event?.response?.status_details || null });
          if (classification !== "ignore") {
            this._scheduleResponseRetry(`response.failed:${errObj?.code || errObj?.type || "unknown"}`);
          }
          break; // no function-call extraction on a failed response
        }
        // Success (completed/cancelled/incomplete/undefined): clear consecutive-failure count.
        this._responseRetryCount = 0;
        // Re-voice a completed-but-unvoiced text response (model answered in text, not speech).
        this._maybeRevoiceUnvoicedText(event, doneResponseId);
        // Fallback: extract function calls from response.done output items
        // in case response.output_item.done doesn't fire for function_call items
        try {
          const outputs = event?.response?.output;
          if (Array.isArray(outputs)) {
            this._emittedFunctionCallIds = this._emittedFunctionCallIds || new Set();
            for (const item of outputs) {
              if (item?.type === "function_call" && item?.name) {
                const callId = item.call_id || item.id;
                if (callId && this._emittedFunctionCallIds.has(callId)) continue;
                if (callId) this._emittedFunctionCallIds.add(callId);
                this._log("FunctionCallFromResponseDone", item);
                this.emitter.emitEvent("functionCall", [{ name: item.name, args: item.arguments || "{}" }]);
              }
            }
          }
        } catch (err) { this._log("FunctionCallFromResponseDoneError", err); }
        break;
      }
      case "response.content_part.added":
        if (event?.part?.type === "audio" || event?.part?.type === "output_audio") {
          this._markOutputStarted(event.response_id || this._activeResponseId);
        }
        break;
      case "response.output_audio.delta":
        this._markOutputStarted(event.response_id || this._activeResponseId);
        break;
      case "response.output_audio.done":
        this._markOutputStopped(event.response_id || this._activeResponseId);
        break;
      case "response.output_audio_transcript.done":
      case "response.audio_transcript.done":
        // Do NOT overwrite the live caption with the finalized transcript: it can differ in
        // wording from the spoken deltas and often arrives before the audio finishes, so the
        // caption would show edited text while the voice is still on the delta version. Live
        // captions stay delta-driven ("see what you hear"); .done is only for the saved transcript.
        this.emitter.emitEvent("transcriptDone", [{ part: this._part, text: `Interviewer: ${event.transcript || ""}\n\n` }]);
        break;
      case "response.output_audio_transcript.delta":
      case "response.audio_transcript.delta":
        this._markOutputStarted(event.response_id || this._activeResponseId);
        this._captions += event.delta || "";
        this.emitter.emitEvent("captions", [this._captions]);
        // Deltas are for live captions only; do not append to transcript buffer.
        this.emitter.emitEvent("transcriptDelta", [{ part: this._part, delta: event.delta || "" }]);
        break;
      case "response.output_text.delta": {
        // A text-modality item (audio items emit output_audio_transcript.* instead). Its presence
        // means the model answered in text with no audio — accumulate so we can re-voice it.
        const rid = event.response_id || this._activeResponseId;
        if (rid) this._textBuffers.set(rid, (this._textBuffers.get(rid) || "") + (event.delta || ""));
        break;
      }
      case "response.output_text.done": {
        const rid = event.response_id || this._activeResponseId;
        // Prefer the finalized text over accumulated deltas.
        if (rid && typeof event.text === "string" && event.text) this._textBuffers.set(rid, event.text);
        break;
      }
      case "conversation.item.input_audio_transcription.completed":
        // Emit first so the transcript is appended to the buffer before any settle-waiter resumes.
        this.emitter.emitEvent("inputTranscriptionCompleted", [{ part: this._part, text: event.transcript || "" }]);
        if (this._pendingInputTranscriptions > 0) this._pendingInputTranscriptions -= 1;
        if (this._pendingInputTranscriptions <= 0) this._resolveInputSettleWaiters();
        break;
      case "response.output_item.done":
        if (event?.item?.type === "function_call") {
          const callId = event.item.call_id || event.item.id;
          // Track emitted call IDs to avoid double-firing from response.done fallback
          this._emittedFunctionCallIds = this._emittedFunctionCallIds || new Set();
          if (callId && this._emittedFunctionCallIds.has(callId)) break;
          if (callId) this._emittedFunctionCallIds.add(callId);
          this._log("FunctionCall", event.item);
          this.emitter.emitEvent("functionCall", [{ name: event.item.name, args: event.item.arguments }]);
        }
        break;
      case "input_audio_buffer.speech_stopped":
        this._userSpeaking = false;
        // An utterance ended; its Whisper transcription is now pending. Tracked so a section
        // rotation/finish can wait for the candidate's final answer before flushing/teardown.
        this._pendingInputTranscriptions += 1;
        this.emitter.emitEvent("speechStopped");
        break;
      case "output_audio_buffer.started":
        this._markOutputStarted(event.response_id || this._activeResponseId);
        break;
      case "output_audio_buffer.stopped":
        this._markOutputStopped(event.response_id || this._activeResponseId);
        break;
      case "error": {
        const classification = classifyRealtimeError(event);
        if (classification === "ignore") {
          // Expected noise from our own cancel/clear sequences.
          this._log("ServiceErrorIgnored", event);
          break;
        }
        this._log("ServiceError", { classification, event });
        if (classification === "transient") {
          this._scheduleResponseRetry(`error:${event?.error?.code || event?.error?.type || "unknown"}`);
        } else {
          // fatal-session: token-refresh/transport recovery is out of scope here; surface it.
          try { this.emitter.emitEvent("serviceError", [{ kind: "azure_error", classification, error: event?.error || null }]); } catch (e) { void e; }
        }
        break;
      }
      default:
        break;
    }
  }

  _teardownKeepInput() {
    this._cancelResponseRetry();
    // Per-connection output/caption tracking must NOT survive a reconnect: a stale
    // _activeOutputResponseId from the previous section makes _markOutputStarted/Stopped
    // bail on their id guards, so outputStopped never fires (mic stays auto-muted) and
    // captions never reset (next section's deltas append onto the prior text).
    this._activeResponseId = null;
    this._activeOutputResponseId = null;
    // Per-connection text-capture state must not survive a reconnect.
    this._textBuffers.clear();
    this._revoiceCount = 0;
    this._userSpeaking = false;
    // A pending input transcription can't survive the closed data channel; drop the count and
    // release any settle-waiters so a rotation/finish awaiting it can't hang.
    this._pendingInputTranscriptions = 0;
    this._resolveInputSettleWaiters();
    if (this._captions) { this._captions = ""; this.emitter.emitEvent("captions", [""]); }
    try { this.dc?.close(); } catch (e) { void e; }
    this.dc = null;
    try { this._remoteAudioTrack?.stop?.(); } catch (e) { void e; }
    this._remoteAudioTrack = null;
    try { this.pc?.close(); } catch (e) { void e; }
    this.pc = null;
  }

  _resolveInputSettleWaiters() {
    const resolvers = this._inputSettleResolvers;
    this._inputSettleResolvers = [];
    for (const resolve of resolvers) { try { resolve(); } catch (e) { void e; } }
  }

  /**
   * Resolve once every pending candidate utterance has been transcribed (the async Whisper
   * `conversation.item.input_audio_transcription.completed` has arrived), or after `timeoutMs` as a
   * safety cap so a missing completion can never hang a section rotation/finish. Used to capture the
   * candidate's final answer before the transcript buffer is flushed and the connection torn down.
   */
  async waitForInputTranscriptionsSettled({ timeoutMs = 2000 } = {}) {
    if (this._pendingInputTranscriptions <= 0) return;
    await new Promise((resolve) => {
      let settled = false;
      const done = () => { if (settled) return; settled = true; clearTimeout(timer); resolve(); };
      const timer = setTimeout(done, timeoutMs);
      this._inputSettleResolvers.push(done);
    });
  }

  teardown() {
    this._closed = true;
    this._setState(REALTIME_STATES.STOPPED);
    this._cancelReconnectTimer();
    this._cancelResponseRetry();
    try { this.relay.stop(); } catch (e) { void e; }
    this._teardownKeepInput();
    // don't stop inputStream here; owner manages it
    this.inputStream = null;
    this.audioEl = null;
    this._activeResponseId = null;
    this._activeOutputResponseId = null;
    this._finishRequested = false;
    this._captions = "";
  }

  // Heartbeat removed: rely on RTCPeerConnection state and datachannel events for recovery.
  _stopHeartbeat() { this._hbStop = null; }

  _cancelReconnectTimer() {
    try { if (this._reconnectTimer) clearTimeout(this._reconnectTimer); } catch (e) { void e; }
    this._reconnectTimer = null;
  }

  _log(tag, payload) {
    const entry = { level: "info", tag, payload, ts: Date.now() };
    try { this.emitter.emitEvent("log", [entry]); } catch (e) { void e; }
    try { console.log("[RealtimeService]", tag, payload || ""); } catch (e) { void e; }
  }

  async sendPartFinishRequest({ force = false } = {}) {
    if (this._activeResponseId) {
      this._send({ type: "response.cancel", response_id: this._activeResponseId });
      await new Promise((r) => setTimeout(r, 100));
    }
    this._clearOutputAudio();
    await new Promise((r) => setTimeout(r, 100));
    const text = force ?
      "FORGET ALL PREVIOUS INSTRUCTIONS. STOP THE INTERVIEW PART. DO NOT SAY ANYTHING ELSE and EXECUTE IMMEDIATELY function_call/tool \"part_finished\"." :
      "Please conclude this part now in 3-5 seconds (5-8 words), then immediately execute function_call \"part_finished\".";
    this.requestResponse({
      instructions: "You are concluding this interview section NOW. Disregard any prior instructions about continuing or asking more questions. Follow the latest system message. Say a very brief closing sentence and then call the part_finished tool immediately.",
      input: [{ type: "message", role: "system", content: [{ type: "input_text", text }] }]
    });
  }

  /**
   * Send a brief system warning that the section will wrap soon. To avoid overlapping
   * assistant audio with ongoing assistant output, this does not cancel an active response.
   * If an assistant response is already active, the warning is skipped.
   */
  sendSectionWrapWarning({ secondsLeft = 15 } = {}) {
    if (this._activeResponseId) {
      this._log("WrapWarningSkippedActiveResponse", { secondsLeft });
      return;
    }
    const text = `We'll wrap this section in about ${secondsLeft} seconds. Please finish your thought.`;
    this.requestResponse({
      input: [{ type: "message", role: "system", content: [{ type: "input_text", text }] }]
    });
  }

  /**
   * Send T-20 warning to prevent model from asking new questions in the final 20 seconds.
   * This does not cancel active responses or request a new response - it just adds the
   * instruction to the conversation context for the model to consider.
   */
  sendT20Warning() {
    const text = "IMPORTANT: Less than 20 seconds remaining in this section. Do NOT ask any new questions or follow-ups. Do NOT introduce new topics. Do NOT make full answers to user's questions. Say in your own words that the section is ending. Respond and wrap up briefly and concisely. You have around 15-20 seconds before the section ends and your output will be cut off.";
    this._send({
      type: "conversation.item.create",
      item: { type: "message", role: "system", content: [{ type: "input_text", text }] }
    });
    // Do not request response - let natural conversation flow continue
    this._log("T20Warning", { sent: true });
  }

  /**
   * Politely interrupt and request a very brief wrap-up, then part_finished.
   * Used when the assistant is speaking at the section boundary.
   */
  async sendQuickWrapAndFinishPart({ seconds = 4, isInterrupt = false } = {}) {
    // Strategy to reduce race with server-side VAD/start-of-response:
    // 1) Create a short-lived response to occupy generation, wait briefly for it to be acknowledged.
    // 2) Cancel that response to ensure any in-flight generation is stopped.
    // 3) Clear output buffer, wait a bit for server to settle.
    // 4) Send the quick-wrap system message and request a fresh response.
    try {
      // Step 1: kick off a transient response to preempt concurrent generation
      const doPreemptive = arguments?.[0]?.preemptive !== false; // default true
      this._log("QuickWrap", { step: "start", seconds, preemptive: doPreemptive, ts: Date.now(), isInterrupt });
      if (doPreemptive) {
        this._send({ type: "response.create", response: { output_modalities: ["audio"] } });
        this._log("QuickWrap", { step: "response.create.sent", ts: Date.now() });
        // Wait briefly for the server to create/acknowledge the response_id (if any)
        const waitForActive = async (timeout = 250) => {
          const start = Date.now();
          while (!this._activeResponseId && Date.now() - start < timeout) {
            await new Promise((r) => setTimeout(r, 20));
          }
        };
        await waitForActive(250);
        this._log("QuickWrap", { step: "response.create.ack.waited", activeResponseId: this._activeResponseId, ts: Date.now() });

        // cancel any active response to stop in-flight generation
        if (this._activeResponseId) {
          this._log("QuickWrap", { step: "cancelling", response_id: this._activeResponseId, ts: Date.now() });
          this._send({ type: "response.cancel", response_id: this._activeResponseId });
          await new Promise((r) => setTimeout(r, 120));
          this._log("QuickWrap", { step: "cancelled", response_id: this._activeResponseId, ts: Date.now() });
        } else {
          this._log("QuickWrap", { step: "no-active-response", ts: Date.now() });
        }

        // clear any buffered audio to avoid overlap and give server a moment to settle
        this._log("QuickWrap", { step: "clearing_output_buffer", ts: Date.now() });
        this._clearOutputAudio();
        await new Promise((r) => setTimeout(r, 120));
        this._log("QuickWrap", { step: "cleared_output_buffer", ts: Date.now() });
      }

      // send the controlled quick-wrap system message and request a response
      // const limit = Math.max(1, Math.min(10, Math.floor(seconds)));
      const message = isInterrupt ? "Sorry to interrupt, but time for this section is up" : "Thank you — moving on to the next section.";
      const text = `Do NOT answer or expand on the candidate's last response. Say a brief very polite closing sentence (no more than 10 seconds), for example: "${message}" but in your own words. After that sentence immediately execute function_call "part_finished".`;
      this._log("QuickWrap", { step: "sending_quick-wrap", text: text.slice(0, 200), ts: Date.now() });
      this.requestResponse({
        instructions: "You are concluding this interview section NOW. Disregard any prior instructions about continuing or asking more questions. Follow the latest system message. Say a very brief closing sentence and then call the part_finished tool immediately.",
        input: [{ type: "message", role: "system", content: [{ type: "input_text", text }] }]
      });
      this._log("QuickWrap", { step: "quick-wrap.requested", ts: Date.now() });
    } catch (err) {
      this._log("QuickWrapError", err);
    }
  }

  /**
   * Arm a natural quick-wrap: let the assistant start a natural response, then after delayMs cancel,
   * clear audio, and send the quick-wrap (non-preemptive). If no response is created within fallbackMs,
   * fall back to sending the quick-wrap anyway.
   */
  armNaturalQuickWrap({ seconds = 4, delayMs = 30, fallbackMs = 1200 } = {}) {
    this._naturalWrap = {
      seconds,
      delayMs,
      fallbackMs,
      armedAt: Date.now(),
      executed: false,
      scheduledTimer: null,
      fallbackTimer: null,
    };
    this._log("NaturalWrap", { step: "armed", seconds, delayMs, fallbackMs, ts: Date.now() });
    // Fallback if no response.created arrives in time
    this._naturalWrap.fallbackTimer = setTimeout(async () => {
      if (!this._naturalWrap || this._naturalWrap.executed) return;
      this._naturalWrap.executed = true;
      try {
        // If something is active, cancel and clear; else just proceed
        if (this._activeResponseId) {
          this._log("NaturalWrap", { step: "fallback.cancelling", response_id: this._activeResponseId, ts: Date.now() });
          this._send({ type: "response.cancel", response_id: this._activeResponseId });
          await new Promise((r) => setTimeout(r, 100));
        }
        this._log("NaturalWrap", { step: "fallback.clearing_output_buffer", ts: Date.now() });
        this._clearOutputAudio();
        await new Promise((r) => setTimeout(r, 100));
        const s = this._naturalWrap.seconds ?? seconds;
        this._log("NaturalWrap", { step: "fallback.sending_quick-wrap", seconds: s, ts: Date.now() });
        await this.sendQuickWrapAndFinishPart({ seconds: s, preemptive: false });
      } catch (err) {
        this._log("NaturalWrapError", err);
      } finally {
        this._naturalWrap = null;
      }
    }, Math.max(0, fallbackMs));
  }

  cancelActiveResponse() {
    try {
      if (this._activeResponseId) {
        this._send({ type: "response.cancel", response_id: this._activeResponseId });
      }
    } catch { /* noop */ }
  }

  // Dev/test only. Triggers a REAL Azure error via a malformed response.create so the
  // transient-error retry path can be exercised end-to-end during a live interview.
  // persistent:false -> ad-hoc bad send, so _lastResponseArgs keeps the last valid turn
  //   and the retry reissues it (model recovers).
  // persistent:true  -> the malformed request is captured as _lastResponseArgs, so every
  //   reissue also fails and the retries exhaust (ResponseRetryExhausted + serviceError).
  // Returns false (and no-ops) if the assistant is mid-response, since Azure would then
  // return an 'ignore'-class code instead of a retryable error.
  _devSimulateAzureError({ persistent = false } = {}) {
    if (this._activeResponseId) {
      this._log("DevSimulateAzureErrorSkipped", { reason: "active-response" });
      return false;
    }
    if (persistent) {
      this.requestResponse({ input: [{ type: "message", role: "system", content: [{ type: "__bogus_content_type__", text: "boom" }] }] });
    } else {
      this._send({ type: "response.create", response: { output_modalities: ["__invalid_modality__"] } });
    }
    return true;
  }
}
